import { TextLinkWidget, } from '@tableau/widgets';
import { useFormikContext } from 'formik';
import React from 'react';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { HelpService } from '~src/commonInfra/services/HelpService';
import { ServerSettingsInstance } from '~src/settings/services/ServerSettings';
import { learnMoreStyle, SettingsStyles } from '~src/settings/styles/CommonStyles';

import { SessionUserSelectors } from '../../../../../bootstrap/redux/selectors/SessionInfoSelectors';
import { Messages } from '../../../../../codegen/Localize';
import { FormikCheckbox } from '../../../../../commonInfra/components/FormikCheckbox';
import { SettingsSection } from '../../../../components/common/SettingsSection';
import { useSelector } from '../../../utils';
import { ServerSettingsProps } from '../../server-settings/ReactServerSettings';
import { componentRegistryEntry } from '../../SettingsComponentRegistryFunctions';

export type FlowParametersServerSettingSectionProps = ServerSettingsProps<'flowParametersEnabled' | 'flowParametersAnyTypeEnabled'>;

export const FLOWPARAMETERS_SERVER_SETTINGS_TESTID = 'flow-parameters-server-setting-section-test-id';
export const FLOWPARAMETERS_SERVER_SETTINGS_HELP_TESTID = 'flow-parameters-server-settings-help-test-id';
export const FLOWPARAMETERS_SERVER_SETTINGS_CHECKBOX_TESTID = 'flow-parameters-on-server-checkbox-test-id';
export const FLOWPARAMETERS_SERVER_SETTINGS_ANYTYPE_CHECKBOX_TESTID = 'flow-parameters-any-type-on-server-checkbox-test-id';
export const FLOWPARAMETERS_SERVER_SETTINGS_ANY_TYPE_HELP_TESTID = 'flow-parameters-any-type-on-server-help-test-id';
/**
 * We were using UpdateBooleanServerSettingRequest.ts for storing these names.
 * However as we decouple, each setting section should have its own setting name
 * Hence added here
 */
export const UpdateBooleanServerSettingNames = {
  flowParametersEnabled: 'flowParametersEnabled',
  flowParametersAnyTypeEnabled: 'flowParametersAnyTypeEnabled',
};

const isFlowParametersServerSettingsVisible = (isServerAdmin: boolean): boolean => {
  return FeatureFlags.FlowParametersEnabled && isServerAdmin;
};

export function FlowParametersServerSettingSection(props: FlowParametersServerSettingSectionProps): JSX.Element {
  const isServerAdmin = useSelector(SessionUserSelectors.isSessionServerAdmin);
  const formik = useFormikContext<FlowParametersServerSettingSectionProps>();

  if (!isFlowParametersServerSettingsVisible(isServerAdmin)) {
    return null;
  }

  return (
    <SettingsSection
      title={Messages.flowParametersSettingHeader()}
      testId={FLOWPARAMETERS_SERVER_SETTINGS_TESTID}
      description={
        <div>
          {Messages.flowParametersServerSettingsBody()}
          <TextLinkWidget
            testId={FLOWPARAMETERS_SERVER_SETTINGS_HELP_TESTID}
            target={'_blank'}
            className={learnMoreStyle}
            href={HelpService.getServerHelpUrlByCategory('prep-conductor-flow-parameters')}>
            {Messages.learnMore()}
          </TextLinkWidget>
        </div>
      }>
      <div className={SettingsStyles.checkboxGroup}>
        <FormikCheckbox
          label={Messages.flowParametersSettingsAllowParametersCheckbox()}
          name={'settings.flowParametersEnabled'}
          testId={FLOWPARAMETERS_SERVER_SETTINGS_CHECKBOX_TESTID}
        />
        <div className={SettingsStyles.settingsSubGroup}>
          <FormikCheckbox
            name={'settings.flowParametersAnyTypeEnabled'}
            label={Messages.flowParametersSettingsAnyTypeParametersCheckbox()}
            testId={FLOWPARAMETERS_SERVER_SETTINGS_ANYTYPE_CHECKBOX_TESTID}
            checked={formik.values.settings.flowParametersEnabled && formik.values.settings.flowParametersAnyTypeEnabled}
            disabled={!formik.values.settings.flowParametersEnabled}
          />
        </div>
      </div>
    </SettingsSection>
  );
}

/* for registering the component in settings registry */
export const FlowParametersServerSettingSpecs = componentRegistryEntry({
  name: 'FlowParametersServerSettingSection',
  component: FlowParametersServerSettingSection,
  onSubmit: async (settings) => {
    const serverSettingsService = ServerSettingsInstance;
    const booleanNames = UpdateBooleanServerSettingNames;
    if (settings.flowParametersEnabled !== undefined) {
      await serverSettingsService.updateBoolean(booleanNames.flowParametersEnabled, settings.flowParametersEnabled)
        .then(() => {
          if (settings.flowParametersEnabled && settings.flowParametersAnyTypeEnabled !== undefined) {
            return serverSettingsService.updateBoolean(booleanNames.flowParametersAnyTypeEnabled, settings.flowParametersAnyTypeEnabled);
          }
        });
    }
  }
});
